import Image from "next/image";
import type React from "react";

interface Step {
  id: string;
  title: string;
  description: string;
  icon: string;
}

interface InstructionsProps {
  className?: string;
}

const steps: Step[] = [
  {
    id: "step1",
    title: "How to Earn Free Spins",
    description:
      "Sharing your unique code—\npost it on socials or send it directly to your friends.\nThey will earn 1 free spin on accepting your invite!",
    icon: "/assets/referral/step1-icon.webp",
  },
  {
    id: "step2",
    title: "Refer & Get 7 Free Spins",
    description:
      "When your friend completes their first In-Game Quest on Immutable Play, they score a FREE Spin, and you receive 7 spins—claimable at a rate of 1 per day!",
    icon: "/assets/referral/step2-icon.webp",
  },
  {
    id: "step3",
    title: "Climb The Leaderboard For Spins",
    description:
      "Stack up points on the Referral Leaderboard. The higher you climb, the more FREE Spins you earn each week",
    icon: "/assets/referral/step3-icon.webp",
  },
  {
    id: "step4",
    title: "Play In-Game Quests, Earn Free Spins",
    description:
      "Complete ANY In-Game Quests on Immutable Play daily to earn FREE spins!",
    icon: "/assets/referral/step4-icon.webp",
  },
];

export const Instructions: React.FC<InstructionsProps> = ({
  className = "",
}) => {
  return (
    <>
      {/* Mobile Layout - Vertical Stack */}
      <div
        className={`content-stretch flex flex-col items-start relative size-full md:hidden ${className}`}
      >
        {steps.map((step, index) => (
          <div
            key={step.id}
            className="content-stretch flex gap-[14px] items-center relative shrink-0 w-full"
          >
            {/* Step Indicator with Icon and Vertical Connecting Lines */}
            <div className="content-stretch flex flex-col gap-[8px] h-[100px] items-center justify-center relative shrink-0">
              {/* Top connecting line - only show if not first step */}
              <div className="basis-0 content-stretch flex flex-col gap-[10px] grow items-start min-h-px min-w-px shrink-0 w-[2px]">
                {index > 0 && (
                  <div className="basis-0 bg-[#d9d9d9] grow min-h-px min-w-px shrink-0 w-full" />
                )}
              </div>

              {/* Step Icon */}
              <div className="overflow-clip relative shrink-0 size-[48px]">
                <Image
                  alt=""
                  src={step.icon}
                  width={48}
                  height={48}
                  className="absolute inset-0 max-w-none object-cover pointer-events-none size-full"
                />
              </div>

              {/* Bottom connecting line - only show if not last step */}
              <div className="basis-0 content-stretch flex flex-col gap-[10px] grow items-start min-h-px min-w-px relative shrink-0 w-[2px]">
                {index < steps.length - 1 && (
                  <div className="basis-0 bg-[#d9d9d9] grow min-h-px min-w-px shrink-0 w-full" />
                )}
              </div>
            </div>

            {/* Step Content */}
            <div className="basis-0 content-stretch flex flex-col gap-[4px] grow items-start min-h-px min-w-px relative shrink-0">
              {/* Step Title */}
              <div className="content-stretch flex items-center relative shrink-0 w-full">
                <p className="basis-0 font-suisse font-semibold grow leading-[24px] min-h-px min-w-px not-italic relative shrink-0 text-[16px] text-neutral-50">
                  {step.title}
                </p>
              </div>

              {/* Step Description */}
              <div className="content-stretch flex items-center relative shrink-0 w-full">
                <p className="basis-0 font-suisse font-normal grow leading-[16px] min-h-px min-w-px not-italic relative shrink-0 text-[#b6b6b6] text-[12px] whitespace-pre-line">
                  {step.description}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop Layout - Horizontal Stack */}
      <div
        className={`content-stretch flex items-start relative size-full hidden md:flex ${className}`}
      >
        {steps.map((step, index) => (
          <div
            key={step.id}
            className="basis-0 content-stretch flex flex-col gap-[14px] grow items-start min-h-px min-w-px relative shrink-0"
          >
            {/* Step Indicator with Icon and Horizontal Connecting Lines */}
            <div className="content-stretch flex gap-[16px] items-center relative shrink-0 w-full">
              {/* Left connecting line - only show if not first step */}
              <div className="basis-0 content-stretch flex flex-col gap-[10px] grow items-start min-h-px min-w-px relative shrink-0">
                {index > 0 && (
                  <div className="bg-[#d9d9d9] h-[2px] shrink-0 w-full" />
                )}
              </div>

              {/* Step Icon */}
              <div className="overflow-clip relative shrink-0 size-[64px]">
                <Image
                  alt=""
                  src={step.icon}
                  width={64}
                  height={64}
                  className="absolute inset-0 max-w-none object-cover pointer-events-none size-full"
                />
              </div>

              {/* Right connecting line - only show if not last step */}
              <div className="basis-0 content-stretch flex flex-col gap-[10px] grow items-start min-h-px min-w-px relative shrink-0">
                {index < steps.length - 1 && (
                  <div className="bg-[#d9d9d9] h-[2px] shrink-0 w-full" />
                )}
              </div>
            </div>

            {/* Step Content */}
            <div className="box-border content-stretch flex flex-col gap-[4px] items-start px-[20px] py-0 relative shrink-0 w-full">
              {/* Step Title */}
              <div className="content-stretch flex items-center relative shrink-0 w-full">
                <p className="basis-0 font-suisse font-semibold grow leading-[24px] min-h-px min-w-px not-italic relative shrink-0 text-[16px] text-center text-neutral-50">
                  {step.title}
                </p>
              </div>

              {/* Step Description */}
              <div className="content-stretch flex items-center relative shrink-0 w-full">
                <p className="basis-0 font-suisse font-normal grow leading-[16px] min-h-px min-w-px not-italic relative shrink-0 text-[#b6b6b6] text-[12px] text-center whitespace-pre-line">
                  {step.description}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default Instructions;
